import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Icon } from '@ui/icons/Icon.tsx';
import { Input2 } from '@ui/inputs/Input2.tsx';
import { SEARCH_RESULTS_CONTAINER_ID } from '@ui/SearchResults/constants.ts';
import { isRtl } from '@utils/dom.ts';

import { useCSSColors } from '../../../../src/hooks/useCSSColors.ts';
import { useIsInitialState } from '../../../../src/hooks/useIsInitialState.tsx';
import { focusElement } from '../../../utils/focusUtils.ts';
import { handleKeyboardSelect } from '../../../utils/keyboardUtils.ts';
import { InputWrapperStyled, NavBarMobileStyled, SearchIcon } from './styles.ts';

const Wrapper = styled.div`
  padding: 10px;
  background: var(--color-widgetBackground);
  width: 100%;
  input {
    border: none;
    border-radius: 8px;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
`;

const BackButtonWrapper = styled.div`
  padding-block: 4px;
  padding-inline: 6px 16px;
`;

const BackButton = styled.button`
  padding: 0;
  border: none;
  background: transparent;
  &:focus-visible {
    outline-offset: -2px;
    border-radius: 4px;
  }
`;

const StyledCustomActionTitle = styled.h1`
  color: var(--color-widgetText);
  font-size: 18px !important;
  margin-inline-start: -4px !important;
  font-weight: 500 !important;
`;

const HeaderMobile = ({
  widgetState,
  handleInputChange,
  onBackButtonClicked,
  handleKeyPress,
  searchPlaceholder,
  T,
}) => {
  const colors = useCSSColors();
  const [state, setWidgetState] = useState(widgetState.getState());

  useEffect(() => widgetState.addCallback(setWidgetState), [widgetState]);

  const inputRef = useRef(null);
  const shouldAutoFocus = !useIsInitialState([state.isSearchConfirmed]);
  const [isInputFocused, setIsInputFocused] = useState(false);

  useEffect(() => {
    if (
      shouldAutoFocus &&
      // focus if nothing else has focus and the search is not confirmed
      // because when search is confirmed, we focus the first result instead
      document.activeElement === document.body &&
      !state.isSearchConfirmed
    ) {
      // Defer focus to the next tick to prevent race conditions with other UI elements (like Toasts).
      setTimeout(() => {
        focusElement('[data-testid="SearchInputBox"]');
      }, 0);
    }
  }, [state.isSearchConfirmed, shouldAutoFocus]);

  const focusInput = () => inputRef.current?.focus();

  const handleClearSearch = () => {
    handleInputChange('');
    setIsInputFocused(true);
    window.requestAnimationFrame(focusInput);
  };

  return (
    <Wrapper data-testid="HeaderMobile">
      <Row>
        <BackButtonWrapper>
          <BackButton
            onClick={onBackButtonClicked}
            title="Navigate Back"
            data-testid="searchBackButton"
            /* Use a different key to avoid automatically focus on back button after switching page */
            key={`back-${state.term}`}
          >
            <Icon
              className="icon"
              width={32}
              height={32}
              name={isRtl() ? 'arrow-right' : 'arrow-left'}
              cdn
              fillColor={colors.widgetIconFill}
            />
          </BackButton>
        </BackButtonWrapper>

        {state.customActionName && state.customActionCategory ? (
          <StyledCustomActionTitle data-testid="CustomActionText" aria-label={`${state.customActionName}`}>
            {state.customActionName}
          </StyledCustomActionTitle>
        ) : (
          <NavBarMobileStyled>
            <InputWrapperStyled $isSearchFocused={isInputFocused}>
              <SearchIcon aria-label={T('headerOnline:Search')} data-testid="searchIcon">
                <Icon name="search" cdn width={32} height={32} fillColor={colors.widgetText} />
              </SearchIcon>
              <Input2
                ref={inputRef}
                data-testid="SearchInputBox"
                color={colors.widgetText}
                placeholder={searchPlaceholder}
                value={state.term}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                T={T}
                aria-autocomplete="list"
                aria-haspopup={false}
                aria-controls={isInputFocused || state.term ? SEARCH_RESULTS_CONTAINER_ID : undefined}
                tooltipArrowPosition="left"
                handleClearButtonKeyDown={handleKeyboardSelect(handleClearSearch)}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => setIsInputFocused(false)}
              />
            </InputWrapperStyled>
          </NavBarMobileStyled>
        )}
      </Row>
    </Wrapper>
  );
};

export default HeaderMobile;
